import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Statistic, Spin, Table, DatePicker, Space, Alert, Typography, Segmented, Tag } from 'antd';
import {
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import {
  PieChart, Pie, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../libs/api';

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'ACT', 'TAS', 'NT'];
const STATE_COLORS = {
  NSW: '#54A2CC', VIC: '#05A677', QLD: '#FA5252', SA: '#f5b759',
  WA: '#8884d8', ACT: '#82ca9d', TAS: '#ffc658', NT: '#d88884',
};
const GEN_YEAR_COLOR = '#54A2CC';

function fmt(v) {
  if (v == null) return '-';
  return Number(v).toLocaleString();
}

function fmtKw(v) {
  if (v == null || v === 0) return '-';
  if (v >= 1000) return `${(v / 1000).toFixed(1)} MW`;
  return `${Math.round(v)} kW`;
}

function fmtKwh(v) {
  if (v == null || v === 0) return '-';
  if (v >= 1000) return `${(v / 1000).toFixed(1)} MWh`;
  return `${Math.round(v)} kWh`;
}

const RADIAN = Math.PI / 180;
function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) {
  if (percent < 0.03) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      <tspan x={x} dy="-0.4em">{name}</tspan>
      <tspan x={x} dy="1.2em">{(percent * 100).toFixed(0)}%</tspan>
    </text>
  );
}

export default function StcStatsTab() {
  const [selectedMonth, setSelectedMonth] = useState(dayjs().subtract(1, 'month'));
  const [fuelType, setFuelType] = useState('solar');
  /**
   * Which question the period is asking.
   *
   * 'registration' is what entered the register that month — activity at the
   * registry, and all this page could ever show. 'installation' is what was
   * installed that month whenever its certificates were lodged, which is the
   * market. April 2026 is the case that makes the difference obvious: the
   * certificate factor stepped down on 1 May, installers raced to beat it, and
   * 87,128 batteries went in that month against about 30,000 either side. On a
   * registration basis that bulge is smeared over the months that follow.
   */
  const [basis, setBasis] = useState('installation');
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedGenMonth, setSelectedGenMonth] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (month, which) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/stc/stored-summary', {
        params: { month: month.format('YYYY-MM'), basis: which },
        timeout: 120000,
      });
      setData(res.data.data);
      setSync(res.data.data?.meta?.sync || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load STC statistics');
      setData(null);
    }
    setLoading(false);
  }, []);

  /**
   * Bring the local copy up to yesterday, then read it.
   *
   * One call fetches a bounded run of days and reports what is left, so a first
   * run with fifteen months to collect shows progress instead of hanging; the
   * loop stops either when nothing is left or after enough rounds to make clear
   * something is wrong, rather than spinning for ever on a failing day.
   */
  const catchUp = useCallback(async () => {
    setSyncing(true);
    try {
      for (let round = 0; round < 40; round += 1) {
        const res = await api.post('/api/stc/sync', null, { timeout: 180000 });
        const r = res.data?.data;
        setSync((prev) => ({ ...(prev || {}), daysRemaining: r?.remaining ?? 0 }));
        if (!r || r.remaining === 0 || (r.fetched === 0 && !r.failed?.length)) break;
      }
    } catch (err) {
      // A sync that cannot run is not a reason to show nothing: the stored
      // history is still worth reading, and the banner says how stale it is.
      setError(err.response?.data?.message || 'Could not reach the REC Registry');
    }
    setSyncing(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await catchUp();
      if (!cancelled) await fetchData(selectedMonth, basis);
    })();
    return () => { cancelled = true; };
    // Deliberately only on mount: the catch-up is for opening the page, and
    // changing the month or the basis below re-reads without re-syncing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData(selectedMonth, basis);
  }, [selectedMonth, basis, fetchData]);

  const handleFuelChange = (val) => {
    setFuelType(val);
    setSelectedState(null);
    setSelectedGenMonth(null);
  };

  const handleMonthChange = (date) => {
    if (!date) return;
    setSelectedMonth(date);
    setSelectedState(null);
    setSelectedGenMonth(null);
  };

  const totals = data?.totals || { solar: {}, battery: {}, byState: {}, byGenMonth: {}, byStateMonth: {} };
  const applicantData = data?.applicantData || { solar: [], battery: [] };

  const isSolar = fuelType === 'solar';
  const capKey = isSolar ? 'kw' : 'kwh';
  const fmtCap = isSolar ? fmtKw : fmtKwh;
  const capLabel = isSolar ? 'kW' : 'kWh';

  // KPI values — respects all active filters (fuel + state + genYear)
  const kpi = useMemo(() => {
    let d;
    if (selectedState && selectedGenMonth) {
      d = totals.byStateMonth?.[`${selectedState}|${selectedGenMonth}`]?.[fuelType];
    } else if (selectedState) {
      d = totals.byState?.[selectedState]?.[fuelType];
    } else if (selectedGenMonth) {
      d = totals.byGenMonth?.[selectedGenMonth]?.[fuelType];
    } else {
      d = totals[fuelType];
    }
    if (!d) d = {};
    const systems = d.systems || 0;
    const stcs = d.stcs || 0;
    const capacity = d[capKey] || 0;
    const avgSize = systems > 0 ? Math.round((capacity / systems) * 10) / 10 : 0;
    return { systems, stcs, capacity, avgSize };
  }, [fuelType, selectedState, selectedGenMonth, totals, capKey]);

  // Pie chart: systems by state (filtered by genYear if active)
  const systemsPieData = useMemo(() => {
    return STATES.map((s) => {
      let val;
      if (selectedGenMonth) {
        val = totals.byStateMonth?.[`${s}|${selectedGenMonth}`]?.[fuelType]?.systems || 0;
      } else {
        val = totals.byState?.[s]?.[fuelType]?.systems || 0;
      }
      return { name: s, value: val };
    }).filter((d) => d.value > 0);
  }, [fuelType, selectedGenMonth, totals]);

  // Pie chart: capacity by state (filtered by genYear if active)
  const capacityPieData = useMemo(() => {
    return STATES.map((s) => {
      let val;
      if (selectedGenMonth) {
        val = totals.byStateMonth?.[`${s}|${selectedGenMonth}`]?.[fuelType]?.[capKey] || 0;
      } else {
        val = totals.byState?.[s]?.[fuelType]?.[capKey] || 0;
      }
      return { name: s, value: val };
    }).filter((d) => d.value > 0);
  }, [fuelType, selectedGenMonth, totals, capKey]);

  /**
   * The trend, over the axis the period selector itself moves along.
   *
   * Separate from the decomposition below on purpose: that one says what the
   * selected month is made of, this one says how the market has moved. One
   * chart cannot be both, and trying to make it both is what made the bar look
   * like it was ignoring the toggle.
   */
  const trendData = useMemo(() => Object.entries(data?.trend || {})
    .map(([month, v]) => {
      const d = dayjs(`${month}-01`);
      return {
        month,
        label: d.isValid() ? d.format('MMM YY') : month,
        systems: v?.[fuelType]?.systems || 0,
      };
    })
    .sort((a, b) => a.month.localeCompare(b.month)), [data, fuelType]);

  const trendTitle = basis === 'installation'
    ? 'Installations per month'
    : 'Registrations per month';

  /**
   * What the bar chart is an axis of.
   *
   * It decomposes the selected period along the axis the period is not on: pick
   * a registration month and it shows which installation months fed it, pick an
   * installation month and it shows when those systems were registered. Naming
   * it "Generation Month" either way was wrong half the time.
   */
  const crossAxisTitle = basis === 'installation'
    ? 'Installed this month, by the month they were registered'
    : 'Registered this month, by the month they were installed';

  // Generation month bar chart (filtered by state if active)
  const genMonthData = useMemo(() => {
    return Object.entries(totals.byGenMonth || {})
      .map(([month, v]) => {
        let systems;
        if (selectedState) {
          systems = totals.byStateMonth?.[`${selectedState}|${month}`]?.[fuelType]?.systems || 0;
        } else {
          systems = v[fuelType]?.systems || 0;
        }
        // Format "2025-12" → "Dec 25"
        const d = dayjs(month + '-01');
        const label = d.isValid() ? d.format('MMM YY') : month;
        return { month, label, systems };
      })
      .filter((d) => d.systems > 0)
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [fuelType, selectedState, totals]);

  // Filtered applicants — respects all active filters
  const filteredApplicants = useMemo(() => {
    const list = applicantData[fuelType] || [];
    if (!selectedState && !selectedGenMonth) return list;
    return list
      .map((a) => {
        let src;
        if (selectedState && selectedGenMonth) {
          src = a.byStateMonth?.[`${selectedState}|${selectedGenMonth}`];
        } else if (selectedState) {
          src = a.byState?.[selectedState];
        } else {
          src = a.byGenMonth?.[selectedGenMonth];
        }
        if (!src || src.systems === 0) return null;
        return { name: a.name, systems: src.systems, stcs: src.stcs, [capKey]: src[capKey] };
      })
      .filter(Boolean)
      .sort((a, b) => b.systems - a.systems);
  }, [fuelType, selectedState, selectedGenMonth, applicantData, capKey]);

  const handlePieClick = (entry) => {
    if (entry?.name) {
      setSelectedState((prev) => (prev === entry.name ? null : entry.name));
    }
  };

  const handleGenMonthClick = (chartData) => {
    const m = chartData?.activePayload?.[0]?.payload?.month;
    if (!m) return;
    setSelectedGenMonth((prev) => (prev === m ? null : m));
  };

  const applicantColumns = [
    { title: '#', key: 'rank', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Company', dataIndex: 'name', key: 'name', ellipsis: true, sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Systems', dataIndex: 'systems', key: 'systems', align: 'right', width: 100, render: fmt, sorter: (a, b) => a.systems - b.systems, defaultSortOrder: 'descend' },
    { title: 'STCs', dataIndex: 'stcs', key: 'stcs', align: 'right', width: 120, render: fmt, sorter: (a, b) => a.stcs - b.stcs },
    {
      title: `Capacity (${capLabel})`,
      dataIndex: capKey,
      key: 'capacity',
      align: 'right',
      width: 140,
      render: fmtCap,
      sorter: (a, b) => (a[capKey] || 0) - (b[capKey] || 0),
    },
  ];

  return (
    <>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        Solar and battery system registrations from the REC Registry
      </div>

      {error && (
        <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }}
          onClose={() => setError(null)} />
      )}

      {(syncing || sync?.daysRemaining > 0) && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message={syncing
            ? `Catching up with the REC Registry${sync?.daysRemaining
              ? ` — ${sync.daysRemaining} days to go` : ''}`
            : `${sync.daysRemaining} days not yet collected; figures end ${sync.lastSyncedDate || 'earlier'}`}
        />
      )}
      {basis === 'installation' && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="The most recent months are still filling in"
          description={'The scheme allows twelve months from installation to create certificates, '
            + 'so a system installed last month may not be counted yet. Recent months on this '
            + 'basis read low and will keep rising — the CER carries the same warning on its own '
            + 'published figures. Switch to Registered to see what actually entered the register.'}
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Segmented
            options={[
              { label: 'Solar', value: 'solar', icon: <HomeOutlined /> },
              { label: 'Battery', value: 'battery', icon: <ThunderboltOutlined /> },
            ]}
            value={fuelType}
            onChange={handleFuelChange}
          />
          <Segmented
            options={[
              { label: 'Installed', value: 'installation' },
              { label: 'Registered', value: 'registration' },
            ]}
            value={basis}
            onChange={(v) => { setBasis(v); setSelectedGenMonth(null); }}
          />
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={handleMonthChange}
            disabledDate={(d) => d.isAfter(dayjs(), 'month')}
            format="MMMM YYYY"
            size="small"
            allowClear={false}
          />
          {selectedState && (
            <Tag closable onClose={() => setSelectedState(null)} color={STATE_COLORS[selectedState]}>
              {selectedState}
            </Tag>
          )}
          {selectedGenMonth && (
            <Tag closable onClose={() => setSelectedGenMonth(null)} color="#8884d8">
              {dayjs(selectedGenMonth + '-01').format('MMM YYYY')}
            </Tag>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {data?.meta?.daysFetched || 0} / {data?.meta?.daysRequested || 0} days
          </Typography.Text>
        </Space>
      </Card>

      <Spin spinning={loading} tip="Fetching STC data... This may take up to 30 seconds.">
        {/* KPI Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title={`${isSolar ? 'Solar' : 'Battery'} Systems`}
                value={kpi.systems}
                formatter={(v) => fmt(v)}
                prefix={isSolar
                  ? <HomeOutlined style={{ color: '#f5b759' }} />
                  : <ThunderboltOutlined style={{ color: '#54A2CC' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title={`Total ${isSolar ? 'Capacity (kW)' : 'Capacity (kWh)'}`}
                value={kpi.capacity}
                formatter={(v) => fmtCap(v)}
                prefix={<SafetyCertificateOutlined style={{ color: '#05A677' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="Total STCs"
                value={kpi.stcs}
                formatter={(v) => fmt(v)}
                prefix={<SafetyCertificateOutlined style={{ color: '#FA5252' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title={`Avg Size (${capLabel})`}
                value={kpi.avgSize}
                precision={1}
                prefix={<SafetyCertificateOutlined style={{ color: '#8884d8' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Pie Charts Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={12}>
            <Card title={`${isSolar ? 'Solar' : 'Battery'} Systems by State`} size="small">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={systemsPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    dataKey="value"
                    label={renderPieLabel}
                    labelLine={false}
                    onClick={(data) => handlePieClick(data)}
                    style={{ cursor: 'pointer' }}
                  >
                    {systemsPieData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={STATE_COLORS[entry.name] || '#999'}
                        opacity={selectedState && selectedState !== entry.name ? 0.3 : 1}
                        stroke={selectedState === entry.name ? '#333' : '#fff'}
                        strokeWidth={selectedState === entry.name ? 3 : 1}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={`${isSolar ? 'Solar Capacity (kW)' : 'Battery Capacity (kWh)'} by State`} size="small">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={capacityPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    dataKey="value"
                    label={renderPieLabel}
                    labelLine={false}
                    onClick={(data) => handlePieClick(data)}
                    style={{ cursor: 'pointer' }}
                  >
                    {capacityPieData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={STATE_COLORS[entry.name] || '#999'}
                        opacity={selectedState && selectedState !== entry.name ? 0.3 : 1}
                        stroke={selectedState === entry.name ? '#333' : '#fff'}
                        strokeWidth={selectedState === entry.name ? 3 : 1}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtCap(v)} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Trend, on the axis the period selector moves along */}
        {trendData.length > 1 && (
          <Card title={trendTitle} size="small" style={{ marginBottom: 16 }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis tickFormatter={fmt} fontSize={11} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Line
                  type="monotone" dataKey="systems" name="Systems"
                  stroke="#54A2CC" strokeWidth={2}
                  dot={{ r: 3 }} activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* What the selected period is made of, along the other axis */}
        {genMonthData.length > 0 && (
          <Card title={crossAxisTitle} size="small" style={{ marginBottom: 16 }}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={genMonthData} onClick={handleGenMonthClick}
                margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis tickFormatter={fmt} fontSize={11} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="systems" name="Systems" radius={[4, 4, 0, 0]}>
                  {genMonthData.map((entry) => (
                    <Cell
                      key={entry.month}
                      fill={GEN_YEAR_COLOR}
                      opacity={selectedGenMonth && selectedGenMonth !== entry.month ? 0.3 : 1}
                      cursor="pointer"
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Applicant Table */}
        <Card
          title={`${isSolar ? 'Solar' : 'Battery'} Applicants${selectedState ? ` \u2014 ${selectedState}` : ''}${selectedGenMonth ? ` \u2014 ${dayjs(selectedGenMonth + '-01').format('MMM YYYY')}` : ''} (${fmt(filteredApplicants.length)})`}
          size="small"
        >
          <Table
            dataSource={filteredApplicants.map((a, i) => ({ ...a, key: i }))}
            columns={applicantColumns}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100], showTotal: (total) => `${total} companies` }}
            size="small"
          />
        </Card>
      </Spin>
    </>
  );
}
